import React from 'react'
import {
  Text,
  TouchableOpacity,
  View,
  StyleSheet,
  GestureResponderEvent
} from 'react-native'
import { Ionicons, AntDesign, Feather, Entypo } from '@expo/vector-icons'

// styles
import { colorPalette, globalStyles } from '../../static/styles/global'

// components
import Card from '../../components/shared/card/Card'
import MyScrollView from '../../components/shared/navigator/MyScrollView'

// store
import { useAuthStore } from '../../stores/auth'
import { useHomeStore } from '../../stores/home'

// translation
import { useTranslation } from 'react-i18next'

interface ProfileScreenProps {
  navigation: {
    navigate: (screen: string) => void
  }
}

interface MenuItem {
  title: string
  screenName?: string
  action?: () => void
  icon: React.ReactNode
  bgColor?: string
  show: boolean
}

type CardProfileProps = {
  title: string
  subTitle?: string
  action?: (event: GestureResponderEvent) => void
  bgColor?: string
  children?: React.ReactNode
}

// React.memo avoids unnecessary re-renders when parent states trigger updates
const CardProfile = React.memo(
  ({
    title,
    subTitle,
    action,
    bgColor,
    children
  }: CardProfileProps): JSX.Element => {
    return (
      <TouchableOpacity
        onPress={action}
        style={styles.CardProfile}
        activeOpacity={0.7}
      >
        <View style={[globalStyles.hDirection, globalStyles.vCenterItems]}>
          {children && (
            <View
              style={[
                styles.iconCircle,
                { backgroundColor: bgColor || colorPalette.primary }
              ]}
            >
              {children}
            </View>
          )}

          <View style={[globalStyles.vDirection, globalStyles.flexOne]}>
            <Text
              style={[
                globalStyles.text,
                globalStyles.fwSemiBold,
                globalStyles.blackTextColor
              ]}
            >
              {title}
            </Text>
            {!!subTitle && (
              <Text
                style={[
                  globalStyles.text,
                  globalStyles.fs12,
                  globalStyles.grayTextColor
                ]}
              >
                {subTitle}
              </Text>
            )}
          </View>
          <View style={[globalStyles.rn6]}>
            <Entypo
              name={'chevron-right'}
              size={22}
              color={colorPalette.secondary}
            />
          </View>
        </View>
      </TouchableOpacity>
    )
  }
)



const ProfileScreen = ({ navigation }: ProfileScreenProps): JSX.Element => {
  const { t } = useTranslation()
  const { logout } = useAuthStore()
  const { toggleProfile, setToggleProfile, homeData } = useHomeStore()

  // Central navigation handler to dry up the code
  const handleNavigation = (screenName: string) => {
    setToggleProfile(!toggleProfile)
    navigation.navigate(screenName)
  }

  // Configuration-driven menu layout for scaling
  const menuItems: MenuItem[] = [
    {
      title: t('profile'),
      screenName: 'ProfileView',
      icon: <Ionicons name={'person-circle-outline'} size={22} color="white" />,
      show: !!homeData?.active,
      bgColor: '#0ca6d4'
    },
    {
      title: t('scheduleTitle'),
      screenName: 'UpdateScheduleAvailability',
      bgColor: '#cb9406',
      icon: <AntDesign name={'calendar'} size={22} color="white" />,
      show: !!(
        homeData?.active &&
        homeData?.isOnboarded &&
        homeData?.hasDefaultAvailability
      )
    },
    {
      title: t('PTO Request & History'),
      screenName: 'EmployeeRequestPTO',
      icon: <Feather name={'calendar'} size={22} color="white" />,
      show: !!(
        homeData?.active &&
        homeData?.isOnboarded &&
        homeData?.isPTORequestAllowed
      ),
      bgColor: '#9e5389'
    },
    {
      title: homeData?.isOnboarded
        ? t('Employee Resources')
        : t('Employee Onboarding Resources'),
      screenName: 'EmployeeResources',
      icon: <Feather name={'user'} size={22} color="white" />,
      show: !!homeData?.active,
      bgColor: '#ee6404'
    },
    {
      title: t('help'),
      screenName: 'Help',
      icon: <AntDesign name={'questioncircleo'} size={22} color="white" />,
      show: !!homeData?.active,
      bgColor: '#79c46d'
    },
    {
      title: t('settings'),
      screenName: 'Languagescreen',
      icon: <AntDesign name={'setting'} size={22} color="white" />,
      show: !!homeData?.active,
      bgColor: '#07495c'
    },
    {
      title: t('Forms'),
      screenName: 'OnboardingForms',
      icon: <Feather name={'file'} size={22} color="white" />,
      show: !!(homeData?.active && homeData?.isOnboarded),
      bgColor: '#0ca6d4'
    }
  ]

  const visibleMenuItems = menuItems.filter((item) => item.show)

  return (
    <MyScrollView>
      <View
        style={[
          globalStyles.primaryBgColor,
          globalStyles.containerBand,
          globalStyles.containerBandMenu
        ]}
      />
      <View style={[globalStyles.containerOverlap, globalStyles.mb4]}>
        <Card>
          {visibleMenuItems.map((item, index) => (
            <React.Fragment key={item.title}>
              {index > 0 && <View style={[globalStyles.mb2]} />}
              <CardProfile
                title={item.title}
                action={
                  item.screenName
                    ? () => handleNavigation(item.screenName!)
                    : item.action
                }
                bgColor={item.bgColor}
              >
                {item.icon}
              </CardProfile>
            </React.Fragment>
          ))}

          {/* Logout component */}
          <View style={[globalStyles.mb2]} />
          <CardProfile title={t('logout')} action={logout} bgColor="#f22b28">
            <AntDesign name={'logout'} size={22} color="white" />
          </CardProfile>
        </Card>
      </View>
    </MyScrollView>
  )
}

const styles = StyleSheet.create({
  CardProfile: {
    backgroundColor: colorPalette.white,
    padding: 16,
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 1, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 6,
    elevation: 4,
  },
  iconCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 10
  }
})

export default ProfileScreen
